import type {AssistantTimelineV2, Message} from './useChatMessageFormatting'
import type {ChunkCompleteMetadata} from '../BedrockSocketProvider'
import {pickFilesChangedFields, type FilesChangedFields} from '../filesChanged'

export interface CancelFinalizeSnapshot extends FilesChangedFields {
  timeline?: AssistantTimelineV2
  toolsUsed?: string[]
}

export function isAssistantRow(msg: Message): boolean {
  return msg.type === 'bot' || msg.type === 'agent-response'
}

/** Index of the last assistant row, or -1. */
export function findLastAssistantIndex(prev: readonly Message[]): number {
  for (let i = prev.length - 1; i >= 0; i -= 1) {
    if (isAssistantRow(prev[i])) {
      return i
    }
  }
  return -1
}

export function findAssistantIndexByRunId(prev: readonly Message[], runId: string): number {
  return prev.findIndex((m) => m.id === runId && isAssistantRow(m))
}

/** Update an existing assistant row by id, or append — avoids duplicate ids in the thread. */
export function upsertAssistantMessageById(
  prev: readonly Message[],
  runId: string,
  build: (existing: Message | undefined) => Message,
): Message[] {
  const idx = findAssistantIndexByRunId(prev, runId)
  if (idx >= 0) {
    const updated = [...prev]
    updated[idx] = build(updated[idx])
    return updated
  }
  return [...prev, build(undefined)]
}

/** Whether inbound stream events should be applied while the user has cancelled the run. */
export function shouldAcceptStreamEvent(cancelled: boolean, isComplete: boolean): boolean {
  if (!cancelled) {
    return true
  }
  return isComplete
}

/** Whether RUN_FINISHED should replace the row with the full server answer. */
export function shouldApplyNormalCompletion(cancelled: boolean, interrupted?: boolean): boolean {
  if (!cancelled) {
    return true
  }
  return interrupted === true
}

/**
 * Apply user Stop to the thread: finalize or create exactly one assistant row for the run.
 */
export function applyUserCancelToThread(
  prev: readonly Message[],
  runId: string | null,
  snapshot: CancelFinalizeSnapshot,
): Message[] {
  const effectiveRunId = runId?.trim() || null
  if (effectiveRunId) {
    return upsertAssistantMessageById(prev, effectiveRunId, (existing) => {
      const base: Message =
        existing ??
        ({
          id: effectiveRunId,
          type: 'bot',
          content: '',
          isStreamingTurn: true,
        } as Message)
      return finalizeBotMessageOnCancelWithMetadata(base, snapshot)
    })
  }
  const idx = findLastAssistantIndex(prev)
  if (idx === -1) {
    return [...prev]
  }
  const updated = [...prev]
  updated[idx] = finalizeBotMessageOnCancelWithMetadata(updated[idx], snapshot)
  return updated
}

/** Finalize an in-flight assistant row when the user clicks Stop. */
export function finalizeBotMessageOnCancel(msg: Message): Message {
  return finalizeBotMessageOnCancelWithMetadata(msg, {})
}

/** Finalize cancel with partial timeline / tool badges for the run footer. */
export function finalizeBotMessageOnCancelWithMetadata(
  msg: Message,
  snapshot: CancelFinalizeSnapshot,
): Message {
  if (msg.type !== 'bot' && msg.type !== 'agent-response') {
    return msg
  }
  const tl =
    snapshot.timeline && snapshot.timeline.entries.length > 0
      ? snapshot.timeline
      : msg.timeline
  const toolsUsed =
    snapshot.toolsUsed?.length ? snapshot.toolsUsed : msg.toolsUsed
  return {
    ...msg,
    ...(tl ? {timeline: tl} : {}),
    ...(toolsUsed?.length ? {toolsUsed} : {}),
    ...pickFilesChangedFields(snapshot),
    streamAlert: {kind: 'cancel', message: 'Response stopped by user'},
    isStreamingTurn: false,
    liveLeadMarkdown: undefined,
    liveWorkingQueue: undefined,
  }
}

/** Whether assistant-ui's post-cancel `setMessages` resync should be ignored. */
export function shouldSuppressAdapterSetMessages(suppressCancelResync: boolean): boolean {
  return suppressCancelResync
}

/** Patch when RUN_INTERRUPTED arrives after user Stop — footer metadata only (keep frozen timeline/content). */
export function patchAssistantAfterUserCancel(
  row: Message,
  metadata?: ChunkCompleteMetadata,
): Message {
  return {
    ...row,
    streamAlert: row.streamAlert ?? {kind: 'cancel', message: 'Response stopped by user'},
    ...(metadata?.tokenUsage ? {tokenUsage: metadata.tokenUsage} : {}),
    ...(metadata?.modelUsed ? {modelUsed: metadata.modelUsed} : {}),
    ...(metadata?.agentRuntimeLabel ? {agentRuntimeLabel: metadata.agentRuntimeLabel} : {}),
    ...(metadata?.cloudUnits !== undefined ? {cloudUnits: metadata.cloudUnits} : {}),
    ...(metadata?.contextUsage ? {contextUsage: metadata.contextUsage} : {}),
    ...pickFilesChangedFields(metadata),
    isStreamingTurn: false,
    liveLeadMarkdown: undefined,
    liveWorkingQueue: undefined,
  }
}
